import type { ProfileConfig } from "@worker/config-schema";
import { el } from "../lib/dom";

/**
 * Username element. Applies one of the username-effect modifiers via a CSS
 * class; the actual effect implementations live in
 * `src/profile/effects/username-effects.ts`.
 */
/**
 * Effects that operate on individual glyphs (scramble, sparkles per-char,
 * matrix rain, etc.) need the username split into `<span class=username__char>`
 * nodes. Effects that paint the whole name as one shape (rainbow gradient,
 * neon glow, glitch split) want the text rendered as a single text node so
 * the gradient / shadow spans the entire word instead of per-letter.
 */
// Effects that require per-glyph <span> wrappers for their runtime
// behavior (per-char transform, char-swap, sparkle overlay, etc.).
// `fuzzy` and `typewriter` were removed from this set:
// - fuzzy is now a pure-CSS keyframe jitter, doesn't need per-char span
// - typewriter is fully driven by `.username--typewriter` CSS rules
const PER_CHAR_EFFECTS = new Set([
  "shuffle",
  "sparkles-black",
  "sparkles-blue",
  "sparkles-pink",
  "sparkles-rainbow",
  "wave",
  "chromatic",
]);

export function mountUsername(
  profile: ProfileConfig["profile"],
): HTMLElement {
  // Project per-effect options into inline CSS custom properties so the
  // stylesheet's rules can read them without another JS pass.
  const opts = profile.usernameEffectOptions;
  const styleParts: string[] = [];
  if (profile.usernameEffect === "neon") {
    styleParts.push(`--neon-color:${opts.neonColor}`);
    styleParts.push(`--neon-intensity:${opts.neonIntensity}`);
  }
  if (profile.usernameEffect === "emboss") {
    styleParts.push(`--emboss-color:${opts.embossColor}`);
  }
  const node = el("span", {
    class: `username username--${profile.usernameEffect} ${
      profile.usernameEffect === "neon" && !opts.neonFlicker ? "username--neon-steady" : ""
    }`.trim(),
    "data-username": profile.username,
    style: styleParts.join(";") || null,
  });
  if (PER_CHAR_EFFECTS.has(profile.usernameEffect)) {
    for (const ch of profile.username) {
      node.append(el("span", { class: "username__char" }, [ch]));
    }
  } else {
    node.append(document.createTextNode(profile.username));
  }
  return node;
}
